import {
  ActivitySettings,
  ActivityType,
  DiagramGeneralSettings,
  FlowlineSettings,
  ResourceActivityPayload,
  PositionType
} from '../diagram-type-styles.model';
import {ArchitectureBaseConfig} from './diagram-architecture-base-config';
import {FlowlineForm} from '../flowline-form.enum';
import {SYSTEM_ARCHITECTURE} from '../../../shared/constants/diagram-subtypes.constants';
import {MAPS_TYPES_ENUM as MAP_TYPES} from '../../../editor-interface/shared/models/map.model';

export class SystemArchitectureConfig extends ArchitectureBaseConfig {
  readonly activityShape = {w: 200, h: 210};
  readonly activityTypes: ActivityType[] = [
    {
      tooltip: 'ASIDE_L.SYSTEM_DEFAULT_ACTIVITY_TOOLTIP',
      icon: 'activity_icon_v4',
      settings: (x?: number, y?: number) => this.activitySettings(x, y)
    },
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.SALESFORCE,
      text: 'Salesforce',
      footerText: 'Customer relationship management platform for sales, service, and marketing'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.MULESOFT,
      text: 'Mulesoft',
      footerText: 'Integration platform that connects data, apps, and systems via APIs and ETL'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.TABLEAU,
      text: 'Tableau',
      footerText: 'Visual analytics platform to explore, share, and act on business data insights'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.SLACK,
      text: 'Slack',
      footerText: 'Messaging and collaboration platform for teams to communicate and share files'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.SALES_CLOUD,
      text: 'Sales Cloud',
      footerText: 'Helps sales teams manage leads, close deals, and grow customer relationships'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.SERVICE_CLOUD,
      text: 'Service Cloud',
      footerText: 'Delivers customer support tools like case tracking, chat, and knowledge base'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.REVENUE_CLOUD,
      text: 'Revenue Cloud',
      footerText: 'Connects sales and finance to manage quoting, billing, and revenue lifecycle'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.INDUSTRY_CLOUD,
      text: 'Industry Cloud',
      footerText: 'Tailored Salesforce solutions for specific verticals like health or finance'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.E_COMMERCE_CLOUD,
      text: 'E-Commerce Cloud',
      footerText: 'Scalable platform for creating seamless online shopping and buying experiences'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.DATA_CLOUD,
      text: 'Data Cloud',
      footerText: 'Unifies and activates real-time customer data from any system or source'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.MARKETING_CLOUD,
      text: 'Marketing Cloud',
      footerText: 'Manages customer journeys with email, ads, and automation across channels'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.AGENTFORCE,
      text: 'Agentforce',
      footerText: 'AI-powered platform to design and deploy agents across workflows and systems'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.HEROKU,
      text: 'Heroku',
      footerText: 'Cloud platform for building, running, and scaling apps in various languages'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.INBOX,
      text: 'Inbox',
      footerText: 'Salesforce-integrated email tool that syncs calendars, emails, and activity'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.DATA,
      text: 'Data',
      footerText: 'System, service, platform, or integration point involved in your architecture'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.BOTS,
      text: 'Bots',
      footerText: 'Automated agents that handle tasks, answer questions, or trigger workflows'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.WEB,
      text: 'Web',
      footerText: 'Connects agents to websites for browsing, scraping, or interacting via UI'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.AUTHENTICATION,
      text: 'Authentication',
      footerText: 'Manages secure access to systems through login, tokens, or credentials'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.FUNCTION,
      text: 'Function/ API',
      footerText: 'Executes external logic or retrieves data using API or serverless function'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.ACCOUNT,
      text: 'Account',
      footerText: "Represents a company or organization you're doing business with"
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.ASSET,
      text: 'Asset',
      footerText: 'Product owned by a customer; used to track post-sales support items'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.CAMPAIGN,
      text: 'Campaign',
      footerText: 'Marketing initiative used to generate Leads and track effectiveness'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.CASE,
      text: 'Case',
      footerText: 'Customer issue or support request needing resolution'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.CONTACT,
      text: 'Contact',
      footerText: 'Individual associated with an Account; typically a customer or lead'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.CONTRACT,
      text: 'Contract',
      footerText: 'Agreement outlining terms and conditions of service or sale.'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.EVENT,
      text: 'Event',
      footerText: 'Scheduled meeting, call, or task on a calendar linked to users/records'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.LEAD,
      text: 'Lead',
      footerText: "Potential customer who has shown interest but isn't yet qualified"
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.OPPORTUNITY,
      text: 'Opportunity',
      footerText: 'Qualified deal or sale in progress tied to an Account'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.ORDER,
      text: 'Order',
      footerText: 'Confirmation of a purchase with product, pricing, and delivery details'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.PRICEBOOK,
      text: 'Pricebook',
      footerText: 'A list of Products with specific prices for quoting and selling'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.PRODUCT,
      text: 'Product',
      footerText: 'Item or service your company sells; used in Quotes and Opportunities'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.QUOTE,
      text: 'Quote',
      footerText: 'A proposed price for products/services offered in an Opportunity'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.SHIPMENT,
      text: 'Shipment',
      footerText: 'Records the fulfillment and delivery details of an Order'
    }),
    this.getResourceActivityType({
      type: SYSTEM_ARCHITECTURE.RESOURCE_ACTIVITY_TYPES.USER,
      text: 'User',
      footerText: 'A person with login credentials and role-based access in Salesforce'
    })
  ];

  generalSettings(): DiagramGeneralSettings {
    return {
      ...super.generalSettings(),
      tagName: 'Architecture',
      allowedDrilldownSubtypes: [
        MAP_TYPES.SYSTEM_ARCHITECTURE,
        MAP_TYPES.INTERACTION_FLOW,
        MAP_TYPES.DATA_MODEL
      ]
    };
  }

  activitySettings(x?: number, y?: number): ActivitySettings {
    return {
      ...super.activitySettings(x, y),
      text: 'System Component',
      attributePadding: true,
      footerText: 'System, service, platform, or integration point involved in your architecture',
      defaultAttributes: [
        {mxKey: 1, text: 'Attribute 1'},
        {mxKey: 2, text: 'Attribute 2'},
        {mxKey: 3, text: 'Attribute 3'}
      ]
    };
  }

  flowlineSettings(x?: number, y?: number, id?: number | string): FlowlineSettings {
    const baseSettings = super.flowlineSettings(x, y, id);

    return {
      ...baseSettings,
      form: {
        shape: FlowlineForm.shape.Orthogonal,
        type: FlowlineForm.type.Solid,
        start: FlowlineForm.end.None,
        end: FlowlineForm.end.FullArrow
      }
    };
  }

  protected getDefaultResources(keys: string[]) {
    return super.getDefaultResources(keys).map(resource => {
      return {
        ...resource,
        main: true
      };
    });
  }

  private getResourceActivityType({type, text, footerText}: ResourceActivityPayload) {
    const {icon} = this.diagramMeta?.defaultResources?.[type] || {};

    return {
      type,
      tooltip: text,
      img: icon,
      settings: (x?: number, y?: number) => ({
        ...this.resourceActivitySettings(x, y),
        text,
        footerText,
        defaultResources: this.getDefaultResources([type])
      })
    };
  }

  private resourceActivitySettings(x?: number, y?: number): ActivitySettings {
    const resourceActivityShape = {w: 200, h: 256};

    return {
      ...this.activitySettings(x, y),
      defaultActivityShape: resourceActivityShape,
      position: this.getPosition(resourceActivityShape, x, y, PositionType.CENTER),
      dragHandlePosition: this.getPosition(resourceActivityShape, x, y, PositionType.DEFAULT)
    };
  }
}
